import {PhoneHQLoader, PhoneHQAvatar, PhoneHQButton} from 'ch3-ui-lib';
import {getUserStatus, getUserStatusName} from "../../utilis/getStatus.ts";
import {getFullName} from "../../utilis/getFullName.ts";
import {LocalTime} from "../LocalTime.tsx";
import {useEffect, useState} from "react";
import {checkStatusExpiration} from "../../utilis/statusUtilis.ts";
import {Clock, Info, Phone} from "lucide-react";
import {UserElement} from "../../store/features/Users/userService.ts";
import {TelcoStatus, UserStatus} from "../../store/features/Team/teamService.ts";
import {useCallService} from "../../services/callService.ts";
import {useDispatch, useSelector} from "react-redux";
import googleCalendarIcon from "../../assets/google-calendar.svg";
import {hexToEmoji} from "../Emoji/emoji.tsx";
import {convertFromISOFormat} from "../../utilis/timeUtilis.ts";
import {UserSettings} from "../../store/features/ProfileSettings/profileSettingsService.ts";
import {EncryptedStatus} from "./EncryptedStatus.tsx";
import {blockCallDuringGroupCall} from "../../services/groupCallBusyGuard.ts";

export interface ConversationStartProps {
    usersData: UserElement | UserSettings | null;
    status: UserStatus | null | undefined;
    setShowInfoPanel: (show: boolean) => void;
}
export const ConversationStart = ({usersData, status, setShowInfoPanel}:ConversationStartProps) => {
    const [isExpired, setIsExpired] = useState(true);

    const {makeCall} = useCallService();
    const dispatch = useDispatch();
    const callState = useSelector((state: any) => state.dialer.callState);

    const { dateString: meetingDateString, timeString: meetingTimeString } = convertFromISOFormat(status?.meetingUserStatus?.dateTo || '');
    const meetingExpires = meetingDateString ? `${meetingDateString}, ${meetingTimeString}` : meetingTimeString;

    useEffect(() => {
        checkStatusExpiration(status || null, setIsExpired);
        const interval = setInterval(() => {
            checkStatusExpiration(status || null, setIsExpired);
        }, 5 * 1000);

        return () => clearInterval(interval);
    }, [status, status?.customUserStatus?.dueDate]);

    const statusStyles = {
        ONLINE: "bg-green-500 w-2.5 h-2.5",
        TALKING: "bg-yellow-500 w-2.5 h-2.5",
        OFFLINE: "bg-fill-subtle w-2.5 h-2.5",
        DND: "bg-red-500 w-2.5 h-2.5",
        UNKNOWN: "bg-fill-subtle w-2.5 h-2.5"
    }
    const handleCall = () => {
        if (blockCallDuringGroupCall(callState, dispatch)) return;
        if (callState !== 'IDLE' || !usersData) return;

        // Handle both UserElement (userId: number) and UserSettings (userId: string | null)
        const userId = 'id' in usersData ? usersData.userId : usersData.userId;

        makeCall({
            msisdn: String(userId),
            callTo: usersData,
            isInternal: true,
        });
    };

    if (!usersData) {
        return <PhoneHQLoader/>;
    }

    return (
        <div className="flex flex-col justify-end h-full pb-[72px] gap-6">
            <div className={' w-full flex justify-start'}>
                <PhoneHQAvatar
                    name={usersData.firstName + ' ' + usersData.lastName}
                    src={usersData?.avatarUrl}
                    size={'128px'}
                    status={getUserStatus(status)}
                />

            </div>

            <div className={'space-y-2 '}>
                <div className={'mb-4'}>
                    <div className="font-semibold text-ink text-xl ">
                        {getFullName(usersData.firstName, usersData.lastName)}
                    </div>
                    <div className={'text-base text-ink-muted text-ellipsis'}>
                        {usersData.position}
                    </div>
                </div>
                <div>
                    <div className="mx-1 text-tiny font-medium text-ink-muted text-ellipsis flex items-center gap-4">
                                          <span>
                                              <span
                                                  className={statusStyles[getUserStatus(status) as TelcoStatus] + ' block rounded-full'}></span>
                                          </span>
                        {getUserStatusName(status)}
                    </div>
                </div>
                {(!isExpired && status?.customUserStatus?.icon) &&
                    <div className="flex w-full rounded hover:cursor-pointer">
                        <div className="flex items-center w-full">
                            <div className="mr-3 !w-4 flex items-center justify-center">
                                {hexToEmoji(status?.customUserStatus?.icon, '')}
                            </div>
                            <div className="flex items-center gap-1 text-sm w-full break-normal">
                                <span>{status?.customUserStatus?.name || 'No status description'}</span>
                            </div>
                        </div>
                    </div>
                }
                {Boolean(status?.meetingUserStatus?.status === 'IN_MEETING') &&
                    <div className="flex items-center pl-1 w-full rounded hover:cursor-pointer">
                        <div className="flex items-center">
                            <div className="w-5 h-5 mr-2">
                                <img className='' src={googleCalendarIcon}/>
                            </div>
                            <div className="flex items-center gap-1 text-sm w-full break-normal">
                                <span>In a meeting</span>
                                <div className="text-sm text-ink-muted">
                                    {meetingExpires ? `until ${meetingExpires}` : ''}
                                </div>
                            </div>
                        </div>
                    </div>
                }
                {usersData.userTimeZone && <div className={'flex gap-4 pl-1 items-center'}>
                    <Clock size={16}/>
                    <LocalTime className={'font-medium text-ink'}
                               timezone={usersData.userTimeZone}/>
                </div>}         
            </div>
            <div className={'flex gap-2'}>
                <PhoneHQButton onClick={() => setShowInfoPanel(true)} className={'group border border-fill-subtle'} variant={'secondary'}>
                    <Info className={' group-hover:bg-canvas !p-0 w-4'}/>
                    View Profile
                </PhoneHQButton>
                <PhoneHQButton onClick={() => handleCall()} className={'group border border-fill-subtle'} variant={'secondary'}>
                    <Phone className={' group-hover:bg-canvas !p-0 w-4'}/>
                    Start a call
                </PhoneHQButton>
            </div>
            <div className={'py-6  border-t border-line'}>
                <EncryptedStatus isEncrypted={true} />
            </div>
        </div>
    )
}